import axios from 'axios';
import toast from 'react-hot-toast';

const api = axios.create({
  baseURL:         '/api',
  withCredentials: true,   // enviar cookies (refresh_token, csrf_token)
  timeout:         15000,
});

// ─── Obtener CSRF token de la cookie ─────────────────────
function getCsrfCookie() {
  return document.cookie
    .split('; ')
    .find(r => r.startsWith('csrf_token='))
    ?.split('=')[1] || '';
}

// ─── Request interceptor — adjuntar token de acceso + CSRF ──
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers['Authorization'] = `Bearer ${token}`;

  // CSRF en todas las mutaciones
  const safeMethods = ['get','head','options'];
  if (!safeMethods.includes(config.method?.toLowerCase())) {
    config.headers['X-CSRF-Token'] = getCsrfCookie();
  }
  return config;
});

// ─── Response interceptor — renovar token si expira ──────
let refreshing = false;
let waitQueue  = [];

function processQueue(error, token = null) {
  waitQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  waitQueue = [];
}

api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;
    // Endpoints de autenticación: su 401 significa "credenciales incorrectas",
    // NO "token caducado". Antes entraban aquí, se intentaba refrescar, el
    // refresh fallaba y se hacía window.location='/login': la página se
    // recargaba entera y el mensaje de error no llegaba a verse nunca. Ese
    // era el "no entra y vuelve al login sin decir nada".
    const esAuth = /\/auth\/(login|refresh|logout|csrf-token)/.test(original?.url || '');

    // 429 = se ha alcanzado el límite de peticiones. NO es sesión caducada:
    // no hay que renovar nada ni mandar a nadie a la pantalla de entrada. Se
    // deja pasar el error para que la pantalla enseñe su aviso y ya está.
    if (error.response?.status === 429) {
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !esAuth
    ) {
      if (refreshing) {
        return new Promise((resolve, reject) => {
          waitQueue.push({ resolve, reject });
        }).then(token => {
          original.headers['Authorization'] = `Bearer ${token}`;
          return api(original);
        }).catch(() => Promise.reject(error));
      }
      original._retry = true;
      refreshing = true;
      try {
        const { data } = await api.post('/auth/refresh', {});
        const newToken = data.accessToken;
        localStorage.setItem('access_token', newToken);
        api.defaults.headers['Authorization'] = `Bearer ${newToken}`;
        processQueue(null, newToken);
        original.headers['Authorization'] = `Bearer ${newToken}`;
        return api(original);
      } catch (err) {
        processQueue(err, null);
        localStorage.removeItem('access_token');
        // Si ya estamos en el login no se recarga: recargar borraría el
        // mensaje de error que se acaba de pintar.
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(err);
      } finally {
        refreshing = false;
      }
    }
    // ─── 403 de CSRF: se pide una cookie nueva y se reintenta ──
    //
    // Pasa cuando la cookie de csrf ha desaparecido con la sesión todavía
    // viva: caducó, el móvil la tiró, o se entró en otra pestaña. Antes esto
    // era un callejón sin salida —el usuario escribía el formulario entero y
    // al guardar se comía "Token CSRF inválido" sin forma de arreglarlo salvo
    // volviendo a entrar—. Ahora se pide una de una y se manda otra vez, y no
    // se entera nadie.
    if (error.response?.status === 403 && !original?._csrfRetry
        && /csrf/i.test(error.response?.data?.error || '')) {
      original._csrfRetry = true;
      try {
        await api.get('/auth/csrf-token');
        return api(original);
      } catch { /* si tampoco se puede, sigue el camino normal de abajo */ }
    }

    // ─── 403 por falta de permisos ─────────────────────────
    // Red de seguridad: si por lo que sea queda visible un botón que la
    // persona no puede usar, el backend lo corta y aquí se avisa con un
    // mensaje claro en vez de fallar en silencio.
    if (error.response?.status === 403) {
      const data = error.response.data || {};
      if (data.permiso || /permiso/i.test(data.error || '')) {
        toast.error(data.error || 'No tienes permisos para esta acción');
      }
    }

    return Promise.reject(error);
  }
);

export default api;
